import { QrCode, Download, AlertCircle, RefreshCw } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui'

interface QRCodeCardProps {
  trolleyId: number
  trolleyName: string
  hasQrCode: boolean
  qrCodeKey?: Nullable<string>
  onDownload: (trolleyId: number, trolleyName: string) => void
  onRegenerate: (trolleyId: number) => void
  isDownloading: boolean
  isDownloadingAll: boolean
}

export function QRCodeCard({
  trolleyId,
  trolleyName,
  hasQrCode,
  qrCodeKey,
  onDownload,
  onRegenerate,
  isDownloading,
  isDownloadingAll,
}: QRCodeCardProps) {
  const { t } = useTranslation()

  return (
    <div className='bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow'>
      <div className='flex flex-col items-center space-y-1 pt-3'>
        {hasQrCode && qrCodeKey ? (
          <div className='w-full flex items-center justify-center'>
            <QRCodeSVG
              value={qrCodeKey}
              size={150}
              level='M'
              bgColor='#ffffff'
              fgColor='#000000'
            />
          </div>
        ) : (
          <div className='relative w-full aspect-square flex items-center justify-center max-h-[150px]'>
            <QrCode className='h-24 w-24 text-gray-300 opacity-50' />
            <div className='absolute bottom-2 left-2 transform flex items-center gap-1.5 px-3 py-1 bg-red-50 border border-red-100 rounded-full shadow-sm whitespace-nowrap'>
              <AlertCircle className='h-3.5 w-3.5 text-red-600' />
              <span className='text-xs font-medium text-red-600'>
                {t('qrCode.generationFailed', 'Generation failed')}
              </span>
            </div>
          </div>
        )}
        <div className='w-full flex items-center justify-between bg-gray-50 rounded-lg px-3 py-3 mt-2'>
          <span className='text-sm font-semibold text-gray-900'>
            {trolleyName}
          </span>
          {hasQrCode ? (
            <Button
              type='button'
              onClick={() => onDownload(trolleyId, trolleyName)}
              disabled={isDownloading || isDownloadingAll}
              variant='ghost'
              className='hover:bg-gray-200 rounded-md p-1.5 h-auto'
              title='Download'
            >
              <Download className='h-4 w-4 text-gray-500' />
            </Button>
          ) : (
            <Button
              type='button'
              onClick={() => onRegenerate(trolleyId)}
              variant='outline'
              className='flex items-center gap-1.5 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800 h-8 px-3 rounded-md text-xs font-medium'
            >
              <RefreshCw className='h-3.5 w-3.5' />
              {t('qrCode.regenerate', 'Regenerate')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
